import { useState, useRef, useCallback } from 'react';
import type { UploadedDocument, PromptFormApiConfig } from '../../types';
import { isWorksheetSourceFile } from '../../documentSubmit';

const UPLOAD_ENDPOINT = '/upload';

export interface UseDocumentUploadOptions {
    apiConfig?: PromptFormApiConfig;
    documents: UploadedDocument[];
    onDocumentsChange: (docs: UploadedDocument[]) => void;
    disabled?: boolean;
}

export function useDocumentUpload({
    apiConfig,
    documents,
    onDocumentsChange,
    disabled = false,
}: UseDocumentUploadOptions) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const triggerUpload = useCallback(() => {
        if (!apiConfig?.baseUrl || disabled || uploading) return;
        fileInputRef.current?.click();
    }, [apiConfig, disabled, uploading]);

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files?.length || !apiConfig?.baseUrl) return;

            setUploading(true);
            const token = apiConfig.getAuthToken?.();
            const url = `${apiConfig.baseUrl.replace(/\/$/, '')}${UPLOAD_ENDPOINT}`;

            try {
                const newDocs: UploadedDocument[] = [];
                for (const file of Array.from(files)) {
                    if (isWorksheetSourceFile(file.name)) {
                        newDocs.push({
                            id: `doc-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                            name: file.name,
                            url: '',
                            size: file.size,
                            sourceFile: file,
                        });
                        continue;
                    }

                    const formData = new FormData();
                    formData.append('file', file);

                    const res = await fetch(url, {
                        method: 'POST',
                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                        body: formData,
                    });
                    if (!res.ok) {
                        const err = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
                        throw new Error(err.error || err.detail || `Upload failed: ${res.status}`);
                    }
                    const data = (await res.json()) as { url?: string; key?: string };
                    const fileUrl = data.url || (data.key ? `${apiConfig.baseUrl}/${data.key}` : '');
                    if (fileUrl) {
                        newDocs.push({
                            id: `doc-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                            name: file.name,
                            url: fileUrl,
                            size: file.size,
                        });
                    }
                }
                if (newDocs.length > 0) {
                    onDocumentsChange([...documents, ...newDocs]);
                }
            } finally {
                setUploading(false);
                e.target.value = '';
            }
        },
        [apiConfig, documents, onDocumentsChange]
    );

    const removeDocument = useCallback(
        (id: string) => {
            onDocumentsChange(documents.filter((d) => d.id !== id));
        },
        [documents, onDocumentsChange]
    );

    return {
        uploading,
        fileInputRef,
        triggerUpload,
        handleFileChange,
        removeDocument,
    };
}
